import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Plus, Trash2, Download, Printer, QrCode, ExternalLink, Pencil, X, MapPin, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function buildPrint(origin, machine, dataUrl) {
  const logo = `${origin}/brand/caneat-logo-white.png`;
  const w = window.open("", "_blank", "width=820,height=1100");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>CAN'EAT - ${machine.label}</title>
    <style>@page{size:A4;margin:0}*{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    body{font-family:Arial,sans-serif}.page{width:210mm;height:297mm;display:flex;flex-direction:column;align-items:center;
    background:linear-gradient(160deg,#FF8A1E,#F3781E 55%,#EC6A0C);color:#fff;padding:22mm 18mm}
    .logo{height:46mm;margin-top:6mm}.hello{font-size:30pt;font-weight:800;margin-top:10mm;text-align:center}
    .sub{font-size:14pt;font-weight:600;margin-top:4mm}.qrbox{background:#fff;border-radius:10mm;padding:10mm;margin-top:14mm;box-shadow:0 10mm 30mm rgba(80,30,0,.25)}
    .qrbox img{display:block;width:90mm;height:90mm}.scan{font-size:22pt;font-weight:800;margin-top:12mm;text-transform:uppercase}
    .machine{font-size:13pt;font-weight:700;margin-top:3mm;background:rgba(255,255,255,.2);padding:3mm 7mm;border-radius:20mm}
    .foot{margin-top:auto;font-size:11pt;font-weight:700;opacity:.9;letter-spacing:2px}</style></head>
    <body><div class="page"><img class="logo" src="${logo}"/><div class="hello">Une petite surprise<br/>t'attend&nbsp;!</div>
    <div class="sub">Scanne &amp; découvre ta CAN'EAT 👀</div><div class="qrbox"><img src="${dataUrl}"/></div>
    <div class="scan">Scanne-moi</div><div class="machine">${machine.label} · ${machine.machine_id}</div><div class="foot">DESSERT IN A CAN</div></div>
    <script>window.onload=function(){setTimeout(function(){window.print()},400)}</script></body></html>`);
  w.document.close();
}

function EditModal({ initial, onClose, onSave }) {
  const isNew = !initial.id;
  const [f, setF] = useState({ machine_id: "", label: "", location: "", description: "", active: true, ...initial });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setErr("");
    try { await onSave(f, isNew); } catch (e) { setErr(e.response?.data?.detail || "Erreur"); setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-t-3xl bg-white p-5 sm:rounded-3xl" data-testid="machine-editor">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="caneat-title text-xl text-caneat-ink">{isNew ? "Nouveau distributeur" : "Modifier le distributeur"}</h3>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full bg-caneat-pageCream"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Identifiant (URL)</label>
            <input disabled={!isNew} value={f.machine_id} onChange={(e) => setF({ ...f, machine_id: e.target.value })} placeholder="machine-02" data-testid="machine-id-input"
              className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange disabled:opacity-60" /></div>
          <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Nom</label>
            <input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="CAN'EAT Gare centrale" data-testid="machine-label-input"
              className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" /></div>
          <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Emplacement</label>
            <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Bruxelles, Gare du Midi"
              className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" /></div>
          <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Description (facultatif)</label>
            <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
              className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" /></div>
          <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="h-5 w-5 accent-caneat-orange" /><span className="text-sm font-bold text-caneat-ink">Actif</span></label>
          {err && <p className="text-sm font-bold text-red-500">{err}</p>}
          <button onClick={save} disabled={busy} data-testid="machine-save" className="flex min-h-[48px] w-full items-center justify-center rounded-full bg-caneat-orange font-bold text-white disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enregistrer"}</button>
        </div>
      </div>
    </div>, document.body);
}

function Card({ m, origin, canEdit, onEdit, onDelete }) {
  const ref = useRef(null);
  const [showQr, setShowQr] = useState(false);
  const url = `${origin}/vending/${m.machine_id}`;
  const getCanvas = () => ref.current?.querySelector("canvas");
  const download = () => { const c = getCanvas(); if (!c) return; const a = document.createElement("a"); a.download = `caneat-qr-${m.machine_id}.png`; a.href = c.toDataURL("image/png"); a.click(); };
  const print = () => { const c = getCanvas(); if (!c) return; buildPrint(origin, m, c.toDataURL("image/png")); };

  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-caneat-cream" data-testid={`machine-${m.machine_id}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="caneat-title truncate text-lg text-caneat-ink">{m.label}</h3>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${m.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`}>{m.active ? "Actif" : "Inactif"}</span>
          </div>
          <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-semibold text-caneat-orange">/vending/{m.machine_id} <ExternalLink className="h-3 w-3" /></a>
          {m.location && <p className="mt-1 flex items-center gap-1 text-xs text-caneat-muted"><MapPin className="h-3 w-3" /> {m.location}</p>}
        </div>
        {canEdit && (
          <div className="flex shrink-0 gap-1">
            <button onClick={() => onEdit(m)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-caneat-orange/10 text-caneat-orange"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => onDelete(m)} data-testid={`delete-machine-${m.machine_id}`} className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-caneat-pageCream py-2"><p className="text-lg font-extrabold text-caneat-ink">{m.scans ?? 0}</p><p className="text-[10px] font-bold text-caneat-muted">Scans</p></div>
        <div className="rounded-xl bg-caneat-pageCream py-2"><p className="text-lg font-extrabold text-caneat-ink">{m.survey_completed ?? 0}</p><p className="text-[10px] font-bold text-caneat-muted">Sondages</p></div>
        <div className="rounded-xl bg-caneat-pageCream py-2"><p className="text-lg font-extrabold text-caneat-ink">{m.product_views ?? 0}</p><p className="text-[10px] font-bold text-caneat-muted">Consult.</p></div>
      </div>

      <div ref={ref} className={showQr ? "mt-4 flex justify-center" : "hidden"}>
        <div className="rounded-xl bg-white p-3 ring-1 ring-caneat-cream"><QRCodeCanvas value={url} size={150} fgColor="#18130E" bgColor="#FFFFFF" level="H" includeMargin /></div>
      </div>

      <div className="mt-3 flex gap-2">
        <button onClick={() => setShowQr((s) => !s)} data-testid={`view-qr-${m.machine_id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-caneat-pageCream px-3 py-2 text-xs font-bold text-caneat-ink"><Eye className="h-4 w-4" /> {showQr ? "Masquer" : "Voir QR"}</button>
        <button onClick={download} data-testid={`download-qr-${m.machine_id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-caneat-orange px-3 py-2 text-xs font-bold text-white"><Download className="h-4 w-4" /> PNG</button>
        <button onClick={print} data-testid={`print-qr-${m.machine_id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-caneat-ink px-3 py-2 text-xs font-bold text-white"><Printer className="h-4 w-4" /> A4</button>
      </div>
    </div>
  );
}

export default function DistributorsManager() {
  const { authHeaders, canEdit } = useAuth();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const origin = window.location.origin;

  const load = async () => { const { data } = await api.get("/admin/machines", { headers: authHeaders() }); setMachines(data); setLoading(false); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (f, isNew) => {
    if (isNew) await api.post("/admin/machines", f, { headers: authHeaders() });
    else await api.put(`/admin/machines/${f.id}`, f, { headers: authHeaders() });
    setEditing(null); load();
  };
  const del = async (m) => { if (!window.confirm(`Supprimer "${m.label}" ?`)) return; await api.delete(`/admin/machines/${m.id}`, { headers: authHeaders() }); load(); };

  if (loading) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-caneat-orange" /></div>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2"><QrCode className="h-6 w-6 text-caneat-orange" /><h1 className="caneat-title text-3xl text-caneat-ink">Distributeurs</h1></div>
        {canEdit && <button onClick={() => setEditing({})} data-testid="add-machine-btn" className="flex items-center gap-2 rounded-full bg-caneat-orange px-4 py-2.5 text-sm font-bold text-white shadow-lg"><Plus className="h-4 w-4" /> Nouveau</button>}
      </div>
      <p className="mb-4 text-sm font-medium text-caneat-muted">URL permanente par distributeur — le QR ne change jamais même si tu modifies produits, sondage ou allergènes.</p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {machines.map((m) => <Card key={m.id} m={m} origin={origin} canEdit={canEdit} onEdit={setEditing} onDelete={del} />)}
      </div>
      {editing && <EditModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
